/**
 * HitlHandback (I4 · Figma 128:140) — the handback form.
 *
 * Instructor closes out the session by:
 *   - Marking one or more "covered" concept chips
 *   - Writing a short "watch for" note the learner sees on their receipt
 *   - Optionally asking the learner for a 1-5 confidence rating (which
 *     gets stored on the same row — the learner's L4 receipt echoes it)
 *
 * On submit → POST /hitl/sessions/:id/handback then nav to
 * /hitl/session/:id/report so the instructor authors the post-escalation
 * report before the queue view.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hitlApi, CoveredChip } from '../../services/hitlApi';
import { styles } from './HitlStyles';

/** Wave 2 canned chip suggestions. Instructor adds free-form chips too. */
const SUGGESTED_CHIPS: CoveredChip[] = [
  { slug: 'debugging-mindset', label: 'Debugging mindset' },
  { slug: 'reading-the-error', label: 'Reading the error message' },
  { slug: 'stepping-through-code', label: 'Stepping through the code' },
  { slug: 'talking-to-the-ai', label: 'Talking to the AI tutor' },
  { slug: 'test-first', label: 'Test-first thinking' },
];

const HitlHandback: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [selected, setSelected] = useState<CoveredChip[]>([]);
  const [customLabel, setCustomLabel] = useState('');
  const [watchFor, setWatchFor] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (chip: CoveredChip) => {
    setSelected((prev) =>
      prev.find((c) => c.slug === chip.slug)
        ? prev.filter((c) => c.slug !== chip.slug)
        : [...prev, chip],
    );
  };

  const addCustom = () => {
    const label = customLabel.trim();
    if (!label) return;
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (!slug) return;
    if (selected.find((c) => c.slug === slug)) return;
    setSelected((prev) => [...prev, { slug, label }]);
    setCustomLabel('');
  };

  const handleSubmit = async () => {
    if (!id) return;
    setSubmitting(true);
    setError(null);
    try {
      await hitlApi.handback({
        session_id: id,
        covered_chips: selected,
        watch_for_note: watchFor.trim() || undefined,
        learner_confidence_rating: rating ?? undefined,
      });
      nav(`/hitl/session/${id}/report`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to handback');
    } finally {
      setSubmitting(false);
    }
  };

  if (!id) return <div style={styles.page}>Missing session id.</div>;

  return (
    <div style={{ ...styles.page, maxWidth: 720 }}>
      <h1 style={styles.h1}>Handback</h1>
      <p style={styles.subtitle}>
        Wrap up the session. The learner sees your covered chips + "watch for"
        note on their receipt.
      </p>

      <div style={styles.card}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Concepts covered</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {SUGGESTED_CHIPS.map((chip) => {
            const isOn = !!selected.find((c) => c.slug === chip.slug);
            return (
              <button
                key={chip.slug}
                onClick={() => toggle(chip)}
                style={{
                  ...styles.amberChip,
                  border: `1px solid #B8600B`,
                  background: isOn ? '#B8600B' : '#F5E6D8',
                  color: isOn ? '#fff' : '#B8600B',
                  cursor: 'pointer',
                }}
              >
                {isOn ? '✓ ' : '+ '}
                {chip.label}
              </button>
            );
          })}
        </div>
        <div style={styles.row}>
          <input
            placeholder="Add a custom chip"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCustom()}
            style={{ ...styles.input, flex: 1 }}
          />
          <button style={{ ...styles.button, ...styles.secondary }} onClick={addCustom}>
            Add
          </button>
        </div>
        {selected.length > 0 && (
          <div style={{ marginTop: 12, ...styles.muted }}>
            {selected.length} chip{selected.length === 1 ? '' : 's'} selected
          </div>
        )}
      </div>

      <div style={styles.card}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Watch-for note</div>
        <textarea
          placeholder="One thing the learner should watch for next…"
          value={watchFor}
          onChange={(e) => setWatchFor(e.target.value)}
          style={{ ...styles.input, minHeight: 100 }}
        />
      </div>

      <div style={styles.card}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Learner confidence (optional)</div>
        <div style={styles.muted}>
          If the learner told you how confident they felt, log it here.
        </div>
        <div style={{ ...styles.row, marginTop: 12 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setRating(rating === n ? null : n)}
              style={{
                ...styles.button,
                width: 40,
                textAlign: 'center',
                ...(rating === n ? styles.primary : styles.secondary),
                padding: '10px 0',
              }}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{ ...styles.card, background: '#FEF2F2', borderColor: '#FCA5A5', color: '#B91C1C' }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button
          style={{ ...styles.button, ...styles.secondary }}
          onClick={() => nav(`/hitl/session/${id}`)}
        >
          Back to session
        </button>
        <button
          disabled={submitting}
          style={{ ...styles.button, ...styles.primary, opacity: submitting ? 0.6 : 1 }}
          onClick={handleSubmit}
        >
          {submitting ? 'Handing back…' : 'Confirm handback'}
        </button>
      </div>
    </div>
  );
};

export default HitlHandback;
